import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useMutation } from 'react-query';
import { ManualTemplatesApi, ManualTemplatesTypes } from '@frontend/api-manual-templates';
import { NotificationSettingsTypes } from '@frontend/api-notification-settings';
import { TemplateTagTypes, TemplateTagUtils } from '@frontend/api-template-tags';
import { TFunction, useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { Alert, ContentLoader, Heading, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { EditMessageTemplate } from '../../components/EditMessageTemplate';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';

const { ManualTemplateTypes } = ManualTemplatesTypes;

type RequestReceivedMessageTemplateProps = {
  locationId: string;
};

const onlineSchedulingRequestTagsList = (t: TFunction, locationName?: string) =>
  TemplateTagUtils.tagList({
    t,
    locationName,
    apptDatetime: dayjs().add(1, 'week').hour(10).minute(0),
    wantedTags: [TemplateTagTypes.DefaultTagKeys.apptDatetime, TemplateTagTypes.DefaultTagKeys.practiceName],
  });

export const RequestReceivedMessageTemplate = ({ locationId }: RequestReceivedMessageTemplateProps) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule-settings');
  const { getScopeName } = useAppScopeStore();
  const { getCFValueByLocationId } = useSchedulingLocationInfo();

  const hasOnlineSchedulingCF = getCFValueByLocationId(locationId, 'onlinescheduling');

  const {
    data: requestTemplateData,
    refetch: refetchRequestTemplate,
    isFetching,
  } = useQuery({
    queryKey: queryKeys.onlineSchedulingRequestTemplate(locationId),
    queryFn: () => ManualTemplatesApi.getManualTemplateByType(ManualTemplateTypes.onlineScheduleRequest, locationId),
    retry: 1,
    enabled: hasOnlineSchedulingCF,
  });

  const onlineSchedulingRequestTags = useMemo(
    () => onlineSchedulingRequestTagsList(t, getScopeName(locationId)),
    [t, locationId]
  );

  const defaultOnlineSchedulingRequestTemplate = t(
    'Hello, this is {{practice_name}}. We have received your request for {{appt_datetime}}. We will message you once it has been reviewed.',
    {
      practice_name: '{practice_name}',
      appt_datetime: '{appt_datetime}',
    }
  );

  const updateRequestTemplate = useMutation({
    mutationFn: ({ newTemplateText, templateSubType }: NotificationSettingsTypes.UpdateTemplateProps) => {
      const standardizedTemplateText = TemplateTagUtils.standardizeTemplateTextTags(t, newTemplateText);
      return ManualTemplatesApi.updateManualTemplateByType(templateSubType, standardizedTemplateText, locationId);
    },
    onSuccess: () => {
      alert.success(t('Success, updated template'));
      refetchRequestTemplate();
    },
    onError: () => {
      alert.error(t('Failed to update template'));
    },
  });

  if (!hasOnlineSchedulingCF) {
    return (
      <Alert type='warning'>
        {t(
          'It looks like you do not have the permission to view this page. No access found for the schedule request feature.'
        )}
      </Alert>
    );
  }

  return (
    <>
      <ContentLoader show={isFetching} />
      <Heading level={2}>{t('Customize Your Message')}</Heading>
      <EditMessageTemplate
        defaultTemplateText={defaultOnlineSchedulingRequestTemplate}
        initialTemplate={{
          template: {
            text: requestTemplateData?.Template || '',
            multi_template_text: requestTemplateData?.Template || '',
            type: 'all',
          },
        }}
        templateSubType={ManualTemplateTypes.onlineScheduleRequest}
        tags={onlineSchedulingRequestTags}
        onSave={updateRequestTemplate}
      />
    </>
  );
};
